import { z } from "zod";

import type {
  BranchManifest,
  VersionResolutionWarning,
  VersionString,
  ViewManifest,
} from "./types";

// ============================================================================
// Primitives
// ============================================================================

/**
 * Runtime counterpart of the {@link import("./types").VersionString VersionString}
 * template-literal type. Validates the `X.Y.Z` semver triplet format.
 *
 * The type assertion is safe — the regex guarantees the output matches the
 * `${number}.${number}.${number}` template literal that Zod cannot infer.
 */
export const VersionStringSchema = z
  .string()
  .regex(
    /^\d+\.\d+\.\d+$/,
    "Expected semver triplet (X.Y.Z)",
  ) as z.ZodType<VersionString>;

/**
 * Zod schema for {@link VersionResolutionWarning} — the enum of warning codes
 * emitted when a manifest-pinned version can't be used and the loader falls
 * back to a module/view's `minVersion`.
 */
export const VersionResolutionWarningSchema = z.enum([
  "module_not_in_manifest",
  "manifest_behind",
  "major_version_mismatch",
]) satisfies z.ZodType<VersionResolutionWarning>;

// ============================================================================
// IDs
// ============================================================================

/**
 * A single `<domain>` or `<name>` segment of a module/view/branch id: a slug
 * that starts with a lowercase letter, ends with a lowercase letter or digit,
 * and contains only lowercase letters, digits, and internal hyphens.
 */
export const ID_SEGMENT = "[a-z]([a-z0-9-]*[a-z0-9])?";

/**
 * Canonical module/view/branch id format: exactly two slug segments joined by
 * a single slash, e.g. `enrollment/change-payment-form`. This is the single
 * source of truth for the id shape — the deploy-bundle executor and the GCS
 * manifest path builders both rely on it.
 *
 * These ids are used verbatim to build GCS object keys, so the regex also
 * rejects extra slashes, empty segments, and path-like values (`.`, `..`)
 * that would otherwise resolve to an unexpected object key.
 */
export const DOMAIN_NAME_ID_REGEX = new RegExp(
  `^${ID_SEGMENT}/${ID_SEGMENT}$`,
);

/** True when `id` matches the canonical `<domain>/<name>` format. */
export function isValidDomainNameId(id: string): boolean {
  return DOMAIN_NAME_ID_REGEX.test(id);
}

/**
 * Zod schema for a `<domain>/<name>` module/view/branch id. Used for the
 * `branchId` / `viewId` fields and the `views` / `modules` record keys in
 * manifests.
 */
export const ManifestIdSchema = z
  .string()
  .min(1)
  .regex(DOMAIN_NAME_ID_REGEX, {
    message:
      'must be of the form <domain>/<name> using lowercase letters, numbers, and hyphens (e.g. "enrollment/change-payment-form")',
  });

// ============================================================================
// Manifest entries
// ============================================================================

export const ManifestEntrySchema = z.object({
  version: VersionStringSchema,
});

// ============================================================================
// Branch manifest
// ============================================================================

export const BranchManifestSchema = z.object({
  branchId: ManifestIdSchema,
  version: VersionStringSchema,
  views: z.record(ManifestIdSchema, ManifestEntrySchema),
  updatedAt: z.string().datetime({ offset: true }).optional(),
  updatedBy: z.string().min(1).optional(),
}) satisfies z.ZodType<BranchManifest>;

// ============================================================================
// View manifest
// ============================================================================

export const ViewManifestSchema = z.object({
  viewId: ManifestIdSchema,
  version: VersionStringSchema,
  modules: z.record(ManifestIdSchema, ManifestEntrySchema),
  updatedAt: z.string().datetime({ offset: true }).optional(),
  updatedBy: z.string().min(1).optional(),
}) satisfies z.ZodType<ViewManifest>;
